/* ======================================================================
   MEMO SEARCH — moteur de recherche storefront
   ----------------------------------------------------------------------
   Deux rôles :

   1) <memo-search> : champ de recherche avec panneau de résultats
      instantanés (suggestions textuelles + fiches produit), recherches
      récentes, navigation clavier.
      Les résultats instantanés viennent de /search/suggest.json, qui —
      contrairement à /search?q= — gère nativement les préfixes
      (« madur » → Madurai) et tolère les fautes de frappe.

   2) Rattrapage de requête (« rescue ») : quand /search?q=… ne renvoie
      AUCUN résultat, on résout une meilleure requête puis on redirige.
      Cascade :
        a. synonymes — vers une collection (bougie → /collections/…)
           ou vers une reformulation de la requête (vanille → vanilla)
        b. suggestion de requête de Shopify (madur → madurai)
        c. joker de préfixe (madur → madur*), uniquement si
           suggest.json reconnaît le terme — sinon on ramène du hors-sujet
        d. échec → état vide enrichi + produits proches
      Sur une page MAIGRE (1 ou 2 résultats), pas de cascade complète : on
      honore une règle de synonyme explicite, sinon on propose simplement le
      terme complet sans rediriger.
      Chaque candidat est vérifié via un compteur ultra-léger
      (sections/memo-search-count.liquid) avant redirection.

   Aucune dépendance. Ne touche pas au reste du thème.
   ====================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Utilitaires                                                         */
  /* ------------------------------------------------------------------ */

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var RECENT_KEY = 'memo_recent_searches';
  var MAX_RECENT = 6;
  var COUNT_SECTION = 'memo-search-count';

  /* Minuscules + suppression des accents : « Odéon » et « odeon » se valent. */
  function norm(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function searchUrl(term, extra) {
    var url = ROOT.replace(/\/$/, '') + '/search?q=' + encodeURIComponent(term) + '&type=product';
    if (extra) {
      Object.keys(extra).forEach(function (key) {
        if (extra[key] != null && extra[key] !== '') {
          url += '&' + key + '=' + encodeURIComponent(extra[key]);
        }
      });
    }
    return url;
  }

  function param(name) {
    try {
      return new URLSearchParams(window.location.search).get(name) || '';
    } catch (e) {
      return '';
    }
  }

  /* Redimensionne une URL CDN Shopify sans perdre le paramètre de version. */
  function cdn(url, width) {
    if (!url) return '';
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (e) {
      return url;
    }
  }

  /* Mise en gras de la portion saisie, insensible aux accents. */
  function highlight(text, query) {
    var safe = esc(text);
    var needle = norm(query);
    if (!needle) return safe;
    var haystack = norm(text);
    var at = haystack.indexOf(needle);
    /* norm() peut décaler les index si la chaîne contient des accents ;
       on ne surligne que quand les longueurs concordent. */
    if (at < 0 || haystack.length !== text.length) return safe;
    return (
      esc(text.slice(0, at)) +
      '<mark>' + esc(text.slice(at, at + needle.length)) + '</mark>' +
      esc(text.slice(at + needle.length))
    );
  }

  /* ------------------------------------------------------------------ */
  /* Recherches récentes                                                 */
  /* ------------------------------------------------------------------ */

  var Recent = {
    all: function () {
      try {
        var raw = JSON.parse(window.localStorage.getItem(RECENT_KEY));
        return Array.isArray(raw) ? raw.filter(function (t) { return typeof t === 'string' && t.trim(); }) : [];
      } catch (e) {
        return [];
      }
    },
    write: function (list) {
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
      } catch (e) { /* mode privé Safari */ }
    },
    add: function (term) {
      var value = String(term || '').trim();
      if (!value) return;
      var list = this.all().filter(function (t) { return norm(t) !== norm(value); });
      list.unshift(value);
      this.write(list);
    },
    remove: function (term) {
      this.write(this.all().filter(function (t) { return t !== term; }));
    },
    clear: function () {
      try { window.localStorage.removeItem(RECENT_KEY); } catch (e) { /* noop */ }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Synonymes                                                           */
  /* ------------------------------------------------------------------ */

  /* Table par défaut : le catalogue est rédigé en anglais, on rattrape
     donc surtout les recherches françaises. Complétable dans l'éditeur
     de thème (réglage « Synonyms » de la section main-search). */
  var DEFAULT_SYNONYMS =
    '# Familles de produits : Shopify n\'indexe pas le type d\'article,\n' +
    '# on renvoie donc vers la collection correspondante.\n' +
    'bougie, bougies, bougie parfumee = /collections/scented-candles\n' +
    'parfum cheveux, brume cheveux = /collections/hair-perfumes\n' +
    'trousse, trousse de voyage = /collections/travel-cases\n' +
    'voyage, format voyage = /collections/travel-sizes\n' +
    'coffret, coffrets, set decouverte = /collections/discovery-sets\n' +
    'echantillon, echantillons, essai = /collections/samples-to-try\n' +
    'nouveaute, nouveautes = /collections/novelties\n' +
    'cadeau, cadeaux = /collections/gift-edits\n' +
    'gravure, graver, personnalisation = /collections/engraving-service\n' +
    '# NE PAS ACTIVER : ces familles ne sont volontairement pas publiees sur la\n' +
    '# boutique en ligne. Les regles sont conservees pour memoire uniquement ;\n' +
    '# activees, elles pointeraient vers des pages 404 (le moteur verifie de\n' +
    '# toute facon la cible avant d\'y envoyer un visiteur, et l\'ignorerait).\n' +
    '# savon, savons = /collections/perfumed-soaps\n' +
    '# gel douche = /collections/body-shower-gels\n' +
    '# lait corps, lotion corps = /collections/body-lotions\n' +
    '# creme mains = /collections/hand-creams\n' +
    '# gel mains = /collections/hand-cleansing-gels\n' +
    '# Notes et familles olfactives : simple reformulation de la requete.\n' +
    'vanille = vanilla\n' +
    'cuir, cuirs = leather\n' +
    'santal, bois de santal = sandalwood\n' +
    'encens = incense\n' +
    'ambre = amber\n' +
    'musc = musk\n' +
    'tabac = tobacco\n' +
    'miel = honey\n' +
    'figue = fig\n' +
    'poivre = pepper\n' +
    'safran = saffron\n' +
    'jasmin = jasmine\n' +
    'tubereuse = tuberose\n' +
    'agrumes, citron = citrus\n' +
    'fleur, fleurs, floral = flower\n' +
    'bois, boise = wood\n' +
    'tigre = tiger\n' +
    'cachemire = cashmere\n' +
    'recharge, recharges = refill\n' +
    'noel = christmas\n' +
    'homme = men\n' +
    'femme = women\n' +
    'soldes, promo = sale\n' +
    'eau de toilette = eau de parfum';

  function parseSynonyms(text) {
    var map = Object.create(null);
    String(text || '').split('\n').forEach(function (line) {
      if (!line.trim() || line.trim().charAt(0) === '#') return;
      var parts = line.split('=');
      if (parts.length < 2) return;
      var target = parts.slice(1).join('=').trim();
      if (!target) return;
      parts[0].split(',').forEach(function (key) {
        var k = norm(key);
        if (k) map[k] = target;
      });
    });
    return map;
  }

  /* Une cible peut être un chemin du site (« /collections/perfumed-soaps »).
     Shopify n'indexe pas le product_type : pour les familles de produits
     — savons, bougies, soins — seule la collection donne un résultat juste. */
  function isPath(target) {
    return /^\/[\w\-/?=&.%]*$/.test(String(target || '')) && String(target).indexOf('//') !== 0;
  }

  /* Renvoie { value, path } ou null si aucune règle ne s'applique.
     La requête entière est testée d'abord (« carte cadeau », « gel douche »),
     puis chaque mot — mais seule la requête entière peut viser un chemin,
     pour ne pas détourner « cadeau pour homme » vers une collection. */
  function applySynonyms(query, map) {
    var whole = map[norm(query)];
    if (whole && norm(whole) !== norm(query)) {
      return { value: whole, path: isPath(whole) };
    }

    var changed = false;
    var rewritten = String(query).split(/\s+/).map(function (token) {
      var hit = map[norm(token)];
      if (hit && !isPath(hit) && norm(hit) !== norm(token)) { changed = true; return hit; }
      return token;
    }).join(' ');

    return changed ? { value: rewritten, path: false } : null;
  }

  /* ------------------------------------------------------------------ */
  /* Requêtes joker                                                      */
  /* ------------------------------------------------------------------ */

  var OPERATORS = { and: 1, or: 1, not: 1 };

  /* « madur » → « madur* » ; « irish leath » → « irish* leath* ».
     On laisse tel quel ce qui ressemble déjà à de la syntaxe avancée. */
  function wildcard(query) {
    if (!query) return null;
    if (/[*"():]/.test(query)) return null;
    var tokens = String(query).trim().split(/\s+/);
    var touched = false;
    var out = tokens.map(function (token) {
      if (token.length < 4 || OPERATORS[token.toLowerCase()]) return token;
      touched = true;
      return token + '*';
    });
    return touched ? out.join(' ') : null;
  }

  /* ------------------------------------------------------------------ */
  /* Accès réseau                                                        */
  /* ------------------------------------------------------------------ */

  /* Suggestions natives Shopify : préfixes et fautes de frappe gérés. */
  function suggest(query, options) {
    var opts = options || {};
    var types = opts.types || 'product,query,collection,page';
    var limit = opts.limit || 8;
    var url = ROOT.replace(/\/$/, '') + '/search/suggest.json' +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=' + types +
      '&resources[limit]=' + limit +
      '&resources[options][unavailable_products]=last';

    return fetch(url, { signal: opts.signal })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        var results = (data && data.resources && data.resources.results) || {};
        return {
          queries: results.queries || [],
          products: results.products || [],
          collections: results.collections || [],
          pages: results.pages || []
        };
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') throw error;
        return { queries: [], products: [], collections: [], pages: [] };
      });
  }

  /* Compteur léger : rend sections/memo-search-count.liquid, qui n'émet
     qu'un commentaire <!--MSC:n-->. Quelques centaines d'octets contre
     ~150 Ko pour la section de résultats complète. */
  /* Une cible « chemin » peut pointer sur une collection dépubliée : on
     s'assure qu'elle répond 200 avant d'y envoyer le visiteur. */
  var pathCache = Object.create(null);

  function pathExists(path) {
    if (path in pathCache) return Promise.resolve(pathCache[path]);
    return fetch(path, { method: 'HEAD' })
      .then(function (response) {
        pathCache[path] = response.ok;
        return response.ok;
      })
      .catch(function () { return false; });
  }

  var countCache = Object.create(null);

  function countResults(query) {
    var key = norm(query);
    if (key in countCache) return Promise.resolve(countCache[key]);

    return fetch(searchUrl(query) + '&section_id=' + COUNT_SECTION)
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (html) {
        var match = html.match(/MSC:(-?\d+)/);
        var count = match ? parseInt(match[1], 10) : -1;
        countCache[key] = count;
        return count;
      })
      .catch(function () { return -1; });
  }

  /* ------------------------------------------------------------------ */
  /* Nettoyage des produits renvoyés par suggest.json                    */
  /* ------------------------------------------------------------------ */

  /* Le catalogue contient des échantillons et doublons de format qui
     portent le même titre que le parfum principal, et des produits
     volontairement masqués (tag hide_from_search). On dédoublonne par
     titre en privilégiant la fiche principale. */
  function cleanProducts(products, limit) {
    var byTitle = Object.create(null);
    var order = [];

    (products || []).forEach(function (product) {
      var tags = (product.tags || []).map(norm);
      if (tags.indexOf('hide_from_search') > -1) return;

      var key = norm(product.title);
      if (!key) return;

      var isSecondary = tags.indexOf('sample') > -1 || /sample|refill|recharge/i.test(product.handle || '');

      if (!(key in byTitle)) {
        byTitle[key] = { product: product, secondary: isSecondary };
        order.push(key);
      } else if (byTitle[key].secondary && !isSecondary) {
        /* On remplace un échantillon par la fiche principale. */
        byTitle[key] = { product: product, secondary: isSecondary };
      }
    });

    return order.map(function (key) { return byTitle[key].product; }).slice(0, limit || 6);
  }

  /* ------------------------------------------------------------------ */
  /* Élément <memo-search>                                               */
  /* ------------------------------------------------------------------ */

  var ICON_SEARCH = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>';
  var ICON_ARROW = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><line x1="4" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg>';

  /* Numérotation « 01, 02… » comme les suggestions du guide. */
  function num(index) {
    return (index < 9 ? '0' : '') + (index + 1);
  }

  function arrowCell() {
    return '<span class="memo-search__row-arrow" aria-hidden="true">' + ICON_ARROW + '</span>';
  }

  /* Une rangée pointillée : première colonne (numéro, ou croix de
     suppression pour les recherches récentes), libellé, flèche.

     ⚠️ Une rangée qui porte une croix contient donc un <button> : son
     conteneur ne peut pas en être un. Imbriquer deux <button> est invalide,
     le navigateur fermait l'extérieur avant l'intérieur et la croix se
     retrouvait éjectée sous la rangée. Ces rangées utilisent un <div> ; la
     navigation se fait de toute façon aux flèches depuis le champ, et
     role="option" reste correct dans un listbox. */
  function row(options) {
    var lead = options.forget
      ? '<button type="button" class="memo-search__row-forget" data-ms-forget="' + esc(options.term) +
        '" aria-label="' + esc(options.removeLabel || 'Remove') + '">&times;</button>'
      : '<span class="memo-search__row-num">' + esc(options.num) + '</span>';

    var tag, attrs;
    if (options.href) {
      tag = 'a';
      attrs = 'href="' + esc(options.href) + '"';
    } else if (options.forget) {
      tag = 'div';
      attrs = 'data-ms-term="' + esc(options.term) + '"';
    } else {
      tag = 'button';
      attrs = 'type="button" data-ms-term="' + esc(options.term) + '"';
    }

    return '<li><' + tag + ' class="memo-search__row-item' +
      (options.forget ? ' memo-search__row-item--tight' : '') + '" ' + attrs + ' role="option">' +
      lead +
      '<span class="memo-search__row-text">' + options.html + '</span>' +
      arrowCell() +
      '</' + tag + '></li>';
  }

  function MemoSearchBox(root) {
    this.root = root;
    this.form = root.querySelector('[data-ms-form]');
    this.input = root.querySelector('[data-ms-input]');
    this.results = root.querySelector('[data-ms-results]');
    this.clearBtn = root.querySelector('[data-ms-clear]');

    if (!this.form || !this.input || !this.results) return;

    var configEl = root.querySelector('[data-ms-config]');
    var config = {};
    try { config = JSON.parse((configEl && configEl.textContent) || '{}'); } catch (e) { config = {}; }

    this.labels = config.labels || {};
    this.synonyms = parseSynonyms(config.synonyms || DEFAULT_SYNONYMS);
    this.minChars = config.minChars || 2;
    this.productLimit = config.productLimit || 6;

    this.options = [];
    this.activeIndex = -1;
    this.debounce = null;
    this.controller = null;
    this.lastQuery = '';

    this.bind();
  }

  MemoSearchBox.prototype.label = function (key, fallback) {
    return this.labels[key] || fallback || '';
  };

  MemoSearchBox.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () { self.onInput(); });
    this.input.addEventListener('focus', function () { self.onFocus(); });
    this.input.addEventListener('keydown', function (event) { self.onKeydown(event); });

    this.results.addEventListener('click', function (event) { self.onResultsClick(event); });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function () {
        self.input.value = '';
        self.root.classList.remove('is-filled');
        self.input.focus();
        self.onInput();
      });
    }

    this.form.addEventListener('submit', function (event) {
      var query = self.input.value.trim();
      if (!query) {
        event.preventDefault();
        self.input.focus();
        return;
      }
      Recent.add(query);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && document.activeElement === self.input) self.reset();
    });

    this.root.classList.toggle('is-filled', !!this.input.value.trim());

    /* iOS Safari zoome la page quand on prend le focus dans un champ dont la
       police fait moins de 16 px. Le champ est donc à 16 px en mobile — et
       comme l'invite reste en Compagnon Roman, une monospace large, le
       placeholder long y serait tronqué : on substitue une version courte. */
    if (this.labels.placeholderShort) {
      var narrow = window.matchMedia('(max-width: 599px)');
      var full = this.input.getAttribute('placeholder');
      var self2 = this;
      var applyPlaceholder = function () {
        self2.input.setAttribute('placeholder', narrow.matches ? self2.labels.placeholderShort : full);
      };
      applyPlaceholder();
      if (narrow.addEventListener) narrow.addEventListener('change', applyPlaceholder);
    }

    /* Sur /search sans recherche, le curseur se place dans le champ pour
       pouvoir taper aussitôt — au pointeur fin seulement, afin de ne pas
       faire surgir le clavier sur mobile. */
    if (this.root.hasAttribute('data-ms-autofocus') &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.skipNextFocus = true;
      try { this.input.focus({ preventScroll: true }); } catch (e) { this.input.focus(); }
    }
  };

  /* Les résultats vivent dans le flux de la carte : « fermer » revient à
     les vider et à rendre la main aux recherches populaires. */
  MemoSearchBox.prototype.reset = function () {
    this.results.innerHTML = '';
    this.root.classList.remove('has-results', 'is-loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.options = [];
    this.activeIndex = -1;
    this.lastQuery = '';
  };

  MemoSearchBox.prototype.show = function (html) {
    this.results.innerHTML = html;
    this.root.classList.add('has-results');
    this.input.setAttribute('aria-expanded', 'true');
    this.collectOptions();
  };

  MemoSearchBox.prototype.onFocus = function () {
    if (this.skipNextFocus) { this.skipNextFocus = false; return; }
    var query = this.input.value.trim();
    if (query.length >= this.minChars) {
      if (norm(query) !== norm(this.lastQuery)) this.onInput();
      return;
    }
    this.renderRecent();
  };

  MemoSearchBox.prototype.onInput = function () {
    var self = this;
    var query = this.input.value.trim();

    this.root.classList.toggle('is-filled', !!query);
    clearTimeout(this.debounce);

    if (query.length < this.minChars) {
      this.root.classList.remove('is-loading');
      if (this.controller) { this.controller.abort(); this.controller = null; }
      this.renderRecent();
      return;
    }

    this.root.classList.add('is-loading');
    this.debounce = setTimeout(function () { self.fetchAndRender(query); }, 180);
  };

  MemoSearchBox.prototype.fetchAndRender = function (query) {
    var self = this;

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    if (!this.root.classList.contains('has-results')) {
      this.show('<div class="memo-search__skeleton"><span></span><span></span><span></span></div>');
    }

    suggest(query, { signal: this.controller.signal, limit: 10 })
      .then(function (data) {
        self.controller = null;
        self.root.classList.remove('is-loading');
        self.lastQuery = query;
        self.renderResults(query, data);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        self.root.classList.remove('is-loading');
        self.reset();
      });
  };

  /* ---------- Recherches récentes ---------- */

  MemoSearchBox.prototype.renderRecent = function () {
    var recent = Recent.all();
    if (!recent.length) { this.reset(); return; }

    var self = this;
    var rows = recent.map(function (term) {
      return row({
        term: term,
        html: esc(term),
        forget: true,
        removeLabel: self.label('remove', 'Remove')
      });
    }).join('');

    this.show(
      '<div class="memo-search__block">' +
        '<p class="memo-search__label">' + esc(this.label('recent', 'Recent searches')) + '</p>' +
        '<ul class="memo-search__rows" role="listbox">' + rows + '</ul>' +
        '<button type="button" class="memo-search__link" data-ms-clear-recent>' +
          esc(this.label('clearRecent', 'Clear recent searches')) +
        '</button>' +
      '</div>'
    );
  };

  /* ---------- Résultats instantanés ---------- */

  MemoSearchBox.prototype.renderResults = function (query, data) {
    var self = this;
    var products = cleanProducts(data.products, this.productLimit);

    var terms = [];
    var seen = Object.create(null);
    data.queries.forEach(function (item) {
      var text = (item.text || '').trim();
      if (text && !seen[norm(text)]) { seen[norm(text)] = 1; terms.push(text); }
    });
    /* Sans requête suggérée, on retombe sur les titres de produits pour
       garder une colonne de suggestions utile. */
    if (!terms.length) {
      products.forEach(function (product) {
        var text = (product.title || '').trim();
        if (text && !seen[norm(text)]) { seen[norm(text)] = 1; terms.push(text); }
      });
    }
    terms = terms.slice(0, 5);

    var collections = (data.collections || []).slice(0, 2);

    if (!terms.length && !products.length && !collections.length) {
      this.show('<p class="memo-search__empty">' +
        esc(this.label('panelEmpty', 'No match yet — try another word.')) + '</p>');
      return;
    }

    var left = '';
    if (terms.length || collections.length) {
      left += '<div class="memo-search__col">';

      if (terms.length) {
        left += '<p class="memo-search__label">' + esc(this.label('suggestions', 'Suggestions')) + '</p>' +
          '<ul class="memo-search__rows" role="listbox">' +
            terms.map(function (term, index) {
              return row({ num: num(index), term: term, html: highlight(term, query) });
            }).join('') +
          '</ul>';
      }

      if (collections.length) {
        left += '<p class="memo-search__label"' + (terms.length ? ' style="margin-top:26px"' : '') + '>' +
            esc(this.label('collections', 'Collections')) + '</p>' +
          '<ul class="memo-search__rows">' +
            collections.map(function (collection, index) {
              return row({ num: num(index), href: collection.url, html: highlight(collection.title, query) });
            }).join('') +
          '</ul>';
      }

      left += '</div>';
    }

    var right = '';
    if (products.length) {
      right += '<div class="memo-search__col">' +
        '<p class="memo-search__label">' + esc(this.label('products', 'Products')) + '</p>' +
        '<ul class="memo-search__products">' +
          products.map(function (product) {
            var image = product.featured_image && product.featured_image.url ? product.featured_image.url : product.image;
            return '<li><a class="memo-search__product" href="' + esc(product.url) + '">' +
              '<span class="memo-search__product-media">' +
                (image ? '<img src="' + esc(cdn(image, 400)) + '" alt="' + esc(product.title) +
                  '" width="200" height="200" loading="lazy">' : '') +
              '</span>' +
              '<span class="memo-search__product-body">' +
                '<span class="memo-search__product-name">' + esc(product.title) + '</span>' +
                (product.type ? '<span class="memo-search__product-meta">' + esc(product.type) + '</span>' : '') +
              '</span>' +
              '</a></li>';
          }).join('') +
        '</ul>' +
      '</div>';
    }

    var both = left && right ? ' has-both' : '';
    this.show(
      '<div class="memo-search__grid' + both + '">' + left + right + '</div>' +
      '<button type="button" class="memo-search__all" data-ms-submit>' +
        esc(this.label('viewAll', 'View all results')) + ICON_ARROW +
      '</button>'
    );
  };

  /* ---------- Navigation clavier ---------- */

  MemoSearchBox.prototype.collectOptions = function () {
    this.options = Array.prototype.slice.call(
      this.results.querySelectorAll('.memo-search__row-item, .memo-search__product')
    );
    this.activeIndex = -1;
  };

  MemoSearchBox.prototype.syncActive = function () {
    var active = this.options[this.activeIndex];
    this.options.forEach(function (option, index) {
      option.classList.toggle('is-active', index === this.activeIndex);
    }, this);
    if (active) active.scrollIntoView({ block: 'nearest' });
  };

  MemoSearchBox.prototype.onKeydown = function (event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.options.length) return;
      event.preventDefault();
      var step = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = Math.max(-1, Math.min(this.options.length - 1, this.activeIndex + step));
      this.syncActive();
      return;
    }

    if (event.key === 'Enter') {
      var active = this.options[this.activeIndex];
      if (!active) return;
      event.preventDefault();
      this.activate(active);
    }
  };

  /* ---------- Interactions ---------- */

  MemoSearchBox.prototype.onResultsClick = function (event) {
    var forget = event.target.closest('[data-ms-forget]');
    if (forget) {
      event.preventDefault();
      Recent.remove(forget.getAttribute('data-ms-forget'));
      this.renderRecent();
      return;
    }

    if (event.target.closest('[data-ms-clear-recent]')) {
      event.preventDefault();
      Recent.clear();
      this.reset();
      return;
    }

    var target = event.target.closest('.memo-search__row-item, .memo-search__product, [data-ms-submit]');
    if (!target) return;

    /* Les liens (produit, collection, terme populaire) suivent leur href :
       on enregistre juste la recherche avant de laisser filer. */
    if (target.tagName === 'A') {
      Recent.add(this.input.value.trim());
      return;
    }

    event.preventDefault();
    this.activate(target);
  };

  MemoSearchBox.prototype.activate = function (element) {
    if (element.hasAttribute('data-ms-submit')) { this.submit(); return; }

    var term = element.getAttribute('data-ms-term');
    if (term) {
      this.input.value = term;
      this.root.classList.add('is-filled');
      this.submit();
      return;
    }

    var href = element.getAttribute('href');
    if (href) {
      Recent.add(this.input.value.trim());
      window.location.href = href;
    }
  };

  MemoSearchBox.prototype.submit = function () {
    var query = this.input.value.trim();
    if (!query) { this.input.focus(); return; }
    Recent.add(query);
    if (this.form.requestSubmit) this.form.requestSubmit();
    else this.form.submit();
  };

  /* ------------------------------------------------------------------ */
  /* Rattrapage de requête sur la page de résultats                      */
  /* ------------------------------------------------------------------ */

  function Rescue(host) {
    this.host = host;
    this.query = host.getAttribute('data-ms-rescue') || '';

    var configEl = host.querySelector('[data-ms-rescue-config]');
    var config = {};
    try { config = JSON.parse((configEl && configEl.textContent) || '{}'); } catch (e) { config = {}; }

    this.synonyms = parseSynonyms(config.synonyms || DEFAULT_SYNONYMS);
    this.labels = config.labels || {};
    this.fallbackHost = host.querySelector('[data-ms-fallback]');
  }

  Rescue.prototype.run = function () {
    var self = this;
    var query = this.query.trim();
    if (!query) { this.giveUp(); return; }

    this.candidates(query)
      .then(function (resolved) {
        if (resolved && resolved.url) {
          window.location.replace(resolved.url);
          return null;
        }
        if (resolved) {
          window.location.replace(searchUrl(resolved.term, {
            qs: query,
            qk: resolved.kind
          }));
          return null;
        }
        return self.showNearby(query);
      })
      .catch(function () { self.giveUp(); });
  };

  /* Cascade de candidats, du plus lisible au plus permissif. Chaque
     candidat est validé par un compteur avant d'être retenu, et chacun est
     aussi tenté en version joker : Shopify n'indexe pas toujours le mot seul
     (« soap » ne renvoie rien, « soap* » renvoie les savons parfumés). */
  Rescue.prototype.candidates = function (query) {
    var self = this;
    var tried = Object.create(null);
    tried[norm(query)] = 1;

    function check(term, kind) {
      if (!term) return Promise.resolve(null);
      var key = norm(term);
      if (tried[key]) return Promise.resolve(null);
      tried[key] = 1;
      return countResults(term).then(function (count) {
        if (count <= 0) return null;
        /* Une requête à joker ne doit jamais être montrée telle quelle au
           visiteur : on l'annonce comme « correspondances approchantes ». */
        return { term: term, kind: term.indexOf('*') > -1 ? 'wildcard' : kind };
      });
    }

    function checkAll(pairs) {
      return pairs.reduce(function (chain, pair) {
        return chain.then(function (found) { return found || check(pair[0], pair[1]); });
      }, Promise.resolve(null));
    }

    /* a. synonymes — purement local, aucun aller-retour inutile */
    var synonym = applySynonyms(query, this.synonyms);

    var rewritten = synonym && !synonym.path ? synonym.value : null;

    var firstStep = synonym && synonym.path
      ? pathExists(synonym.value).then(function (ok) {
          return ok ? { url: synonym.value, kind: 'collection' } : null;
        })
      : Promise.resolve(null);

    return firstStep
      .then(function (hit) {
        if (hit) return hit;
        return checkAll([[rewritten, 'synonym'], [wildcard(rewritten), 'synonym']]);
      })
      .then(function (hit) {
        if (hit) return hit;

        /* b. suggestions de requête Shopify (préfixes + fautes de frappe) */
        return suggest(query, { types: 'query,product', limit: 6 }).then(function (data) {
          self.suggested = data;
          var terms = data.queries
            .map(function (item) { return (item.text || '').trim(); })
            .filter(Boolean)
            .slice(0, 3);

          return checkAll(terms.map(function (term) { return [term, 'suggest']; }));
        });
      })
      .then(function (hit) {
        if (hit) return hit;

        /* c. joker de préfixe — mais SEULEMENT si le moteur prédictif de
           Shopify reconnaît lui-même le terme. Sans ce garde-fou, un mot
           absent du catalogue ramène n'importe quoi : sur Memo US,
           « savon* » renvoyait « Saffron sunset » et « Cappadocia ».
           suggest.json ne renvoie rien du tout pour savon / soap / noel /
           zqxwvu, et au contraire des produits pour madur / maduray /
           irish leath : c'est le bon discriminant. */
        var recognised = self.suggested &&
          (self.suggested.queries.length > 0 || self.suggested.products.length > 0);
        if (!recognised) return null;

        return check(wildcard(query), 'wildcard');
      });
  };

  /* Dernier recours : proposer les produits les plus proches trouvés par
     suggest.json, qui est plus permissif que /search. */
  Rescue.prototype.showNearby = function (query) {
    var self = this;
    var pending = this.suggested ? Promise.resolve(this.suggested) : suggest(query, { types: 'product', limit: 8 });

    return pending.then(function (data) {
      var products = cleanProducts(data.products, 4);
      if (!products.length || !self.fallbackHost) { self.giveUp(); return; }

      self.fallbackHost.innerHTML =
        '<p class="memo-search__label">' + esc(self.labels.nearby || 'You may also like') + '</p>' +
        '<ul class="memo-search__products">' +
          products.map(function (product) {
            var image = product.featured_image && product.featured_image.url ? product.featured_image.url : product.image;
            return '<li><a class="memo-search__product" href="' + esc(product.url) + '">' +
              '<span class="memo-search__product-media">' +
                (image ? '<img src="' + esc(cdn(image, 400)) + '" alt="' + esc(product.title) +
                  '" width="200" height="200" loading="lazy">' : '') +
              '</span>' +
              '<span class="memo-search__product-body">' +
                '<span class="memo-search__product-name">' + esc(product.title) + '</span>' +
                (product.type ? '<span class="memo-search__product-meta">' + esc(product.type) + '</span>' : '') +
              '</span>' +
              '</a></li>';
          }).join('') +
        '</ul>';

      self.fallbackHost.hidden = false;
      self.giveUp();
    });
  };

  /* Révèle l'état vide qui était masqué pendant la tentative. */
  Rescue.prototype.giveUp = function () {
    this.host.classList.remove('is-rescuing');
  };

  /* ------------------------------------------------------------------ */
  /* Bandeau « requête corrigée »                                        */
  /* ------------------------------------------------------------------ */

  /* Liquid n'a pas accès aux paramètres d'URL : le bandeau est rempli ici
     à partir de ?qs= (ce que le visiteur avait tapé) et ?qk= (méthode de
     rattrapage). Sans ces paramètres, il n'y a rien à annoncer. */
  function renderNotice(host) {
    var original = param('qs');
    if (!original) return;

    var kind = param('qk');
    var resolved = param('q');
    var configEl = document.querySelector('[data-ms-config]');
    var labels = {};
    try { labels = (JSON.parse((configEl && configEl.textContent) || '{}').labels) || {}; } catch (e) { labels = {}; }

    function line(template, term) {
      return '<p class="memo-search__notice-line">' +
        esc(template || '').replace('__TERM__', '<em>' + esc(term) + '</em>') + '</p>';
    }

    var html = '';

    if (kind === 'wildcard') {
      /* Le rattrapage a élargi la requête d'origine : une seule phrase, et
         pas de lien « chercher quand même » qui renverrait au même endroit. */
      if (labels.approxFor) html += line(labels.approxFor, original);
    } else {
      if (labels.noExact) html += line(labels.noExact, original);
      if (resolved && labels.showingFor) html += line(labels.showingFor, resolved);
      if (labels.searchInstead) {
        html += '<a href="' + esc(searchUrl(original, { qs: original })) + '">' +
          esc(labels.searchInstead || '').replace('__TERM__', esc(original)) + '</a>';
      }
    }

    if (!html) return;

    host.innerHTML = html;
    host.hidden = false;

    /* Le champ, le décompte et le titre de l'onglet doivent refléter ce que
       le visiteur reconnaît, pas la syntaxe d'une requête à joker. */
    if (kind === 'wildcard' && resolved) {
      document.querySelectorAll('[data-ms-input]').forEach(function (input) {
        input.value = original;
      });

      var count = document.querySelector('[data-ms-count]');
      if (count) count.textContent = count.textContent.replace(resolved, original);

      document.title = document.title.replace(resolved, original);
    }
  }

  /* ------------------------------------------------------------------ */
  /* « Vouliez-vous dire… » sur une page de résultats trop maigre         */
  /* ------------------------------------------------------------------ */

  /* Le rattrapage ne se déclenche qu'à ZÉRO résultat. Or une requête
     partielle peut en renvoyer un ou deux au lieu de la série complète :
     sur Memo US, « madur » renvoie 1 fiche Madurai là où « madurai » en
     renvoie 8. On ne redirige pas — la page n'est pas fausse — on propose
     simplement le terme complet.

     Garde-fous : uniquement 1 ou 2 résultats, uniquement si la suggestion
     diffère vraiment, et seulement si elle rapporte nettement plus. */
  var FEW_RESULTS_MAX = 2;
  var GAIN_FACTOR = 2;
  var GAIN_MIN = 3;

  function improveThinPage(host) {
    var query = host.getAttribute('data-ms-few') || '';
    var current = parseInt(host.getAttribute('data-ms-count-value') || '0', 10);
    if (!query || current < 1 || current > FEW_RESULTS_MAX) return;

    var configEl = document.querySelector('[data-ms-config]');
    var config = {};
    try { config = JSON.parse((configEl && configEl.textContent) || '{}'); } catch (e) { config = {}; }
    var labels = config.labels || {};
    var synonyms = parseSynonyms(config.synonyms || DEFAULT_SYNONYMS);

    /* 1) Une règle de synonyme est une consigne explicite du marchand : on
       l'honore même si Shopify a renvoyé un ou deux résultats. Sur Memo US,
       « bougie » renvoie 2 fiches au lieu des 6 bougies de la collection. */
    var synonym = applySynonyms(query, synonyms);

    var viaSynonym = Promise.resolve(false);
    if (synonym && synonym.path) {
      viaSynonym = pathExists(synonym.value).then(function (ok) {
        if (!ok) return false;
        window.location.replace(synonym.value);
        return true;
      });
    } else if (synonym) {
      viaSynonym = countResults(synonym.value).then(function (count) {
        if (count < GAIN_MIN || count < current * GAIN_FACTOR) return false;
        window.location.replace(searchUrl(synonym.value, { qs: query, qk: 'synonym' }));
        return true;
      });
    }

    /* 2) Sinon on se contente de PROPOSER le terme complet : la page n'est
       pas fausse, seulement incomplète — pas de redirection. */
    viaSynonym
      .then(function (redirected) {
        if (redirected || !labels.didYouMean) return null;

        return suggest(query, { types: 'query', limit: 3 }).then(function (data) {
          var candidates = data.queries
            .map(function (item) { return (item.text || '').trim(); })
            .filter(function (text) { return text && norm(text) !== norm(query); })
            .slice(0, 2);

          return candidates.reduce(function (chain, term) {
            return chain.then(function (found) {
              if (found) return found;
              return countResults(term).then(function (count) {
                var worthIt = count >= GAIN_MIN && count >= current * GAIN_FACTOR;
                return worthIt ? { term: term } : null;
              });
            });
          }, Promise.resolve(null));
        });
      })
      .then(function (better) {
        if (!better) return;
        host.innerHTML = '<p class="memo-search__notice-line">' +
          '<a href="' + esc(searchUrl(better.term)) + '">' +
          esc(labels.didYouMean).replace('__TERM__', '<em>' + esc(better.term) + '</em>') +
          '</a></p>';
        host.hidden = false;
      })
      .catch(function () { /* silencieux : c'est un bonus, pas une fonction */ });
  }

  /* ------------------------------------------------------------------ */
  /* Amorçage                                                           */
  /* ------------------------------------------------------------------ */

  function init(scope) {
    var container = scope || document;

    container.querySelectorAll('[data-ms-root]').forEach(function (root) {
      if (root.dataset.msReady) return;
      root.dataset.msReady = '1';
      new MemoSearchBox(root);
    });

    container.querySelectorAll('[data-ms-notice]').forEach(function (host) {
      if (host.dataset.msNoticeReady) return;
      host.dataset.msNoticeReady = '1';

      /* ?qs= : on arrive d'un rattrapage, on explique la substitution.
         Sinon, page maigre : on propose éventuellement un meilleur terme. */
      if (param('qs')) renderNotice(host);
      else improveThinPage(host);
    });

    container.querySelectorAll('[data-ms-rescue]').forEach(function (host) {
      if (host.dataset.msRescueReady) return;
      host.dataset.msRescueReady = '1';

      /* ?qs= signifie qu'on arrive déjà d'un rattrapage : ne pas boucler. */
      if (param('qs')) {
        host.classList.remove('is-rescuing');
        return;
      }

      new Rescue(host).run();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Rechargement d'une section depuis l'éditeur de thème */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });

  window.MemoSearch = { init: init, norm: norm, wildcard: wildcard, applySynonyms: applySynonyms };
})();
